import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Alert, Box, Button, Checkbox, Chip, FormControlLabel, IconButton, MenuItem, Stack, TextField } from "@mui/material";
import { ArrowBackRounded, DownloadRounded } from "@mui/icons-material";
import { useCurrentEditor } from "@tiptap/react";
import { useSnackbar } from "notistack";
import LegalbirdTipTapEditorProvider from "../LbEditor/LegalbirdTipTapEditorProvider";
import LegalbirdTipTapEditor from "../LbEditor/LegalbirdTipTapEditor";
import ButtonLoading from "../Button/ButtonLoading";
import { useCase } from "../../provider/CaseProvider";
import { useDrawer } from "../../provider/DrawerProvider";
import { previewCaseFile } from "../../services/Files/fileService";
import { DocumentExecutionStepPayload } from "../../types/AIChat/DocumentExecutionStepPayload";
import useDraftAutosave from "../../hooks/useDraftAutosave";
import DraftAutosaveIndicator from "../GlobalDrawer/DraftAutosaveIndicator";
import { buildAddress, getDocumentName } from "../Case/CasePdfGenerator/casePdfGeneratorService";
import { useQuery } from "@tanstack/react-query";
import { fetchAvailableDocumentRecipients, getDocumentRecipientRole } from "../../services/documentRecipientService";
import documentEditorPanelStyles from "./documentEditorPanelStyles";

type DocumentEditorPanelProps = {
  // This payload provides the initial values when the drawer creates a new editor instance.
  payload: DocumentExecutionStepPayload;
  // This callback saves the edited document back to its execution step.
  overwrittenHandleSave?: (payload: DocumentExecutionStepPayload) => Promise<void> | void;
};

export default function DocumentEditorPanel({ payload, overwrittenHandleSave }: DocumentEditorPanelProps) {
  return (
    <LegalbirdTipTapEditorProvider initialContent={payload.bodyHtml || ""} placeholder={"Entwurf"}>
      <DocumentEditorPanelContent payload={payload} overwrittenHandleSave={overwrittenHandleSave} />
    </LegalbirdTipTapEditorProvider>
  );
}

function DocumentEditorPanelContent({ payload, overwrittenHandleSave }: DocumentEditorPanelProps) {
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewIsLoading, setPreviewIsLoading] = useState(false);
  const previewMode = !!previewSrc;

  const { product } = useCase();
  const { drawerHeaderNode, registerDraftSave } = useDrawer();
  const { editor } = useCurrentEditor();
  const { enqueueSnackbar } = useSnackbar();
  const [recipientType, setRecipientType] = useState(payload.recipientType ?? "");
  const [saveAsDraft, setSaveAsDraft] = useState(payload.saveAsDraft ?? false);
  const isInternalMemo = payload.documentType === "internalMemo";
  const isRecipientlessMemo = isInternalMemo && !recipientType;
  const filename = getDocumentName(recipientType, payload.filename, payload.recipientType ?? "", product ? getDocumentRecipientRole(product, recipientType) : null);
  const { data: availableRecipients = [], isLoading, isError } = useQuery({
    queryKey: ["available_document_recipients", product?.productClassName, product?.id],
    queryFn: () => fetchAvailableDocumentRecipients(product!),
    enabled: !!product && !isRecipientlessMemo,
  });
  const selectableRecipients = availableRecipients.filter((recipient) => recipient.recipientType !== "custom" || !!payload.address);
  const { saveState, scheduleSave, flushSave } = useDraftAutosave({
    enabled: !!overwrittenHandleSave,
    persist: async (editorHtml) => {
      if (editorHtml === null) {
        return;
      }
      await overwrittenHandleSave?.({
        ...payload,
        filename,
        recipientType: recipientType || null,
        address: recipientType === "custom" ? payload.address : null,
        bodyHtml: editorHtml,
        ...(!isInternalMemo && { saveAsDraft }),
        lastEditedBy: "user",
      });
    },
    editor,
  });

  // Register for this editor's lifetime so closing waits for its pending PATCH.
  useEffect(() => {
    if (overwrittenHandleSave) {
      return registerDraftSave(flushSave);
    }
  }, []);

  if (!product) {
    return null;
  }

  const openPreview = async () => {
    if (!editor || (!isRecipientlessMemo && (isLoading || isError))) {
      return;
    }
    if (!recipientType && !isInternalMemo) {
      enqueueSnackbar("Bitte einen Empfänger auswählen.", { variant: "error" });
      return;
    }
    setPreviewIsLoading(true);

    const previewParams = {
      documentType: payload.documentType,
      title: payload.title,
      content: editor.getHTML(),
      filename,
      description: payload.description,
      recipientType: recipientType || null,
      address: buildAddress({ ...payload, recipientType }),
      externalReference: payload.externalReference ?? null,
      caseReferenceNumber: payload.caseReferenceNumber ?? null,
      insurancePolicyNumber: payload.insurancePolicyNumber ?? null,
      senderMedium: null,
      externalLawyer: null,
    };

    try {
      const src = await previewCaseFile({
        product,
        documentType: "pdf",
        documentIdentifier: "customPdf",
        params: previewParams,
      });
      setPreviewSrc(src);
    } catch (error) {
      console.error("Fehler bei der PDF-Vorschau. Verwendete Parameter:", previewParams);
      console.error("Error Details:", error);

      enqueueSnackbar("PDF-Vorschau konnte nicht erstellt werden. Bitte prüfen Sie, ob der Empfänger vollständig gepflegt ist, und versuchen Sie es erneut.", {
        variant: "error",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } finally {
      setPreviewIsLoading(false);
    }
  };

  return (
    <Stack sx={{ width: "100%", marginTop: "1rem" }} spacing={1}>
      {drawerHeaderNode && createPortal(<DraftAutosaveIndicator saveState={saveState} />, drawerHeaderNode)}
      {!isRecipientlessMemo && isError && <Alert severity="error">Die verfügbaren Empfänger konnten nicht geladen werden.</Alert>}
      {isRecipientlessMemo ? <Chip label="Internes Memo · kein Empfänger" /> : <TextField
        select
        label="Empfänger"
        value={recipientType}
        disabled={isLoading || isError || !overwrittenHandleSave}
        onChange={(event) => {
          setRecipientType(event.target.value);
          setPreviewSrc("");
          scheduleSave();
        }}
      >
        {selectableRecipients.map((recipient) => (
          <MenuItem key={recipient.recipientType} value={recipient.recipientType}>{recipient.label}</MenuItem>
        ))}
      </TextField>}
      <Stack direction={"row"} sx={{ alignItems: "center" }} spacing={1}>
        {previewMode ? (
          <>
            <Button startIcon={<ArrowBackRounded />} onClick={() => setPreviewSrc("")}>
              Zurück zum Editor
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton component={"a"} href={previewSrc} download={filename + ".pdf"}>
              <DownloadRounded />
            </IconButton>
          </>
        ) : (
          <>
            <Chip size={"small"} label={"Entwurf"} />
            <Box sx={{ flexGrow: 1 }} />
            {!isInternalMemo && <FormControlLabel
              label="Als Entwurf speichern"
              control={<Checkbox
                checked={saveAsDraft}
                disabled={!overwrittenHandleSave}
                onChange={(event) => {
                  setSaveAsDraft(event.target.checked);
                  scheduleSave();
                }}
              />}
            />}
            <ButtonLoading variant={"outlined"} fullWidth={false} onClick={openPreview} isLoading={previewIsLoading} disabled={!isRecipientlessMemo && (isLoading || isError)}>
              PDF-Vorschau
            </ButtonLoading>
          </>
        )}
      </Stack>
      {previewMode ? (
        <Box sx={documentEditorPanelStyles.previewBackground}>
          <Box sx={documentEditorPanelStyles.previewEmbedContainer}>
            <embed src={previewSrc} type="application/pdf" width="100%" height="100%" />
          </Box>
        </Box>
      ) : (
        <LegalbirdTipTapEditor editorSx={documentEditorPanelStyles.editor} />
      )}
    </Stack>
  );
}
